/**
 * Installation of Ridalo on the home screen — logic only (no UI, no notification permission).
 *
 *  - Android / desktop Chrome & Edge: the native prompt (`beforeinstallprompt`), kept until
 *    the user clicks « Installer ».
 *  - iPhone / iPad (no such event): simple « Ajouter à l'écran d'accueil » instructions.
 *  - Never repetitive: « Plus tard » hides the offer for 30 days, a second dismissal ends it
 *    for good; an installed app (or one already running installed) never sees it again.
 * State lives in localStorage and holds no credential: { dismissals, lastDismissedAt, installed }.
 */
export const STATE_KEY = 'ridalo:pwaInstall';
export const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
export const MAX_DISMISSALS = 2;

const DEFAULT_STATE = { dismissals: 0, lastDismissedAt: 0, installed: false };

export const readState = (store = window.localStorage) => {
  try {
    const raw = JSON.parse(store.getItem(STATE_KEY) || 'null');
    return raw && typeof raw === 'object' ? { ...DEFAULT_STATE, ...raw } : { ...DEFAULT_STATE };
  } catch {
    return { ...DEFAULT_STATE };
  }
};
const writeState = (state, store = window.localStorage) => {
  try { store.setItem(STATE_KEY, JSON.stringify(state)); } catch { /* private mode: offer simply may return */ }
  return state;
};
export const markInstalled = (store) => writeState({ ...readState(store), installed: true }, store);
export const markDismissed = (now = Date.now(), store) => {
  const s = readState(store);
  return writeState({ ...s, dismissals: s.dismissals + 1, lastDismissedAt: now }, store);
};

/** Already running as an installed app (any display mode but a browser tab). */
export const isStandalone = (win = window) => {
  try {
    if (win.navigator && win.navigator.standalone === true) return true;
    const mm = (q) => !!(win.matchMedia && win.matchMedia(q).matches);
    return mm('(display-mode: standalone)') || mm('(display-mode: fullscreen)') || mm('(display-mode: minimal-ui)');
  } catch {
    return false;
  }
};

/** iPhone / iPad (including iPadOS 13+, which announces itself as a Mac). */
export const isIos = (nav = navigator) => {
  const ua = nav.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1);
};
/** In-app browsers (Facebook, Instagram, LINE…) have no « Add to Home Screen ». */
export const isInAppBrowser = (nav = navigator) => /FBAN|FBAV|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|GSA\//.test(nav.userAgent || '');

/** May the offer be shown now (ignoring which kind)? */
export const mayOffer = ({ standalone, state, now = Date.now() }) => {
  if (standalone || state.installed) return false;
  if (state.dismissals >= MAX_DISMISSALS) return false;
  return !state.lastDismissedAt || now - state.lastDismissedAt >= SNOOZE_MS;
};

/** 'native' | 'ios' | null */
export const offerKind = ({ standalone, state, hasNativePrompt, ios, inApp, now }) => {
  if (!mayOffer({ standalone, state, now })) return null;
  if (hasNativePrompt) return 'native';
  if (ios && !inApp) return 'ios';
  return null;
};

// ── capture of the native prompt (the event may fire before React renders) ──
let deferred = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

export const getDeferredPrompt = () => deferred;
export const subscribeInstall = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

let captured = false;
export const initInstallCapture = (win = typeof window !== 'undefined' ? window : null) => {
  if (!win || captured) return;
  captured = true;
  win.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();           // we show our own discreet offer instead of the browser's mini-bar
    deferred = event;
    emit();
  });
  win.addEventListener('appinstalled', () => {
    deferred = null;
    markInstalled();
    emit();
  });
};

/** Opens the native install dialog. Resolves 'accepted' | 'dismissed' | 'unavailable'. */
export const promptInstall = async () => {
  const ev = deferred;
  if (!ev || typeof ev.prompt !== 'function') return 'unavailable';
  deferred = null;
  emit();
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    const outcome = choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
    if (outcome === 'accepted') markInstalled(); else markDismissed();
    return outcome;
  } catch {
    return 'unavailable';
  }
};

// test helper
export const __resetInstallCapture = () => { deferred = null; captured = false; listeners.clear(); };
